// Lib
import { get, isEmpty } from 'lodash/fp';
import { keys, mapValues } from 'lodash';

// Services
import { http } from '../utils/services/http';
import { setCurrentUserToken, setRememberedBetterAuthSessionUser } from '../auth/authService';
import {
    downgradeBetterAuthSessionToLegacyIfUnflagged,
    upgradeLegacySessionToBetterAuthIfFlagged,
} from '../auth/betterAuthSessionMigrationService';
import {
    fetchCurrentUserAppInitialisationData,
    fetchCurrentUserBoardNotificationPreferencesHttp,
    fetchCurrentUserInitialCustomTemplatesHttp,
    fetchMe,
    updateCurrentUserEmail,
    updateMe,
} from './userService';
import {
    clearInvalidBetterAuthSession,
    getCurrentUserAuthSessionState,
    getFetchMeConfig,
} from './currentUserAuthUtils';

// Actions
import { simpleLogout } from '../auth/authLogoutActions';
import { trackForcedLogoutIfLapsed } from '../analytics/forcedLogoutTracking';
import { loadElements } from '../element/actions/elementActions';
import { navigateToErrorPage } from '../reducers/navigationActions';
import { updateUser } from '../../common/users/userActions';
import { trackUsers, loadUsers } from './userActions';
import { setElementCount } from './elementCount/elementCountActions';
import { appSetInitialised } from '../app/initialisation/initialisationActions';
import { fetchCurrentUserSubscription } from './currentUserSubscriptionActions';
import { boardHierarchiesLoad } from '../element/board/hierarchy/boardHierarchiesActions';
import { currentBoardIdSet } from '../reducers/currentBoardId/currentBoardIdActions';

// Selectors
import { getCurrentUserId } from './currentUserSelector';
import {
    getAclIdsSelector,
    getClosestPermissionIdForElementIdSelector,
} from '../utils/permissions/permissionsSelector';
import { getCurrentBoardId, getIsCurrentBoardIdInitialised } from '../reducers/currentBoardId/currentBoardIdSelector';
import { isBoardPreviewEnabledSelector } from '../workspace/boardPreview/boardPreviewSelector';

// Utils
import globalLogger from '../logger/logger';
import { getDeviceId } from '../device/deviceService';
import { shouldDisableCache } from '../debug/debugUtil';
import { getTimestamp, TIMES } from '../../common/utils/timeUtil';
import { isElementId } from '../../common/uid/idValidator';
import { getElements } from '../element/selectors/elementSelector';
import { canRead } from '../../common/permissions/permissionUtil';
import { getPermission } from '../../common/permissions/elementPermissionsUtil';
import { commonImageUpload } from '../element/attachments/attachmentActions';
import { getNewTransactionId } from '../utils/undoRedo/undoRedoTransactionManager';
import { loadBoardNotificationPreferences } from '../notifications/preferences/boardNotificationPreferencesActions';
import { isGuest } from './userUtil';
import { getUserId } from '../../common/users/utils/userPropertyUtils';
import { getLocalLastNavigationId } from './navigationHistory/localNavigationHistoryService';

// Constants
import {
    CURRENT_USER_SET,
    CURRENT_USER_GET_DETAILS,
    CURRENT_USER_GET_FAILURE,
    CURRENT_USER_SET_GUEST,
} from './currentUserConstants';

import {
    USER_NAVIGATE,
    GUEST_NAVIGATE,
    USER_CHECKED_NOTIFICATIONS,
    USER_CHECKED_QUICK_NOTES,
} from '../../common/users/userConstants';
import { IMAGE_TYPES, IMAGE_TYPE_CONFIGS } from '../../common/media/mediaConstants';
import { METHODS } from '../../common/utils/http/httpConstants';
import { UserNavigationOperation } from '../../common/users/userNavigationTypes';
import { getRootType } from '../../common/elements/utils/elementPropertyUtils';
import { WORKSPACE_SECTIONS as ROOT_TYPES } from '../workspace/workspaceConstants';
import { isUnauthenticatedCurrentUserError, shouldReportCurrentUserAuthFailure } from './currentUserAuthErrorUtils';
import { setUserAnalytics as setUserAnalyticsAction } from '../../common/users/userAnalyticsActions';

const logger = globalLogger.createChannel('current-user-actions');

export const setCurrentUser = (user) => ({
    type: CURRENT_USER_SET,
    user,
});

export const setUserAnalytics = (analytics) =>
    setUserAnalyticsAction({
        analytics,
    });

export const updateCurrentUser =
    ({ changes, userId, personal = false, sync = true }) =>
    (dispatch, getState) =>
        dispatch(
            updateUser({
                userId: userId || getState().getIn(['app', 'currentUser', '_id']),
                changes,
                personal,
                sync,
            }),
        );

export const userCheckedNotifications = ({ userId, timestamp = getTimestamp() }) => ({
    type: USER_CHECKED_NOTIFICATIONS,
    timestamp,
    userId,
    sync: true,
});

export const userCheckedQuickNotes = ({ userId, timestamp = getTimestamp() }) => ({
    type: USER_CHECKED_QUICK_NOTES,
    timestamp,
    userId,
    sync: true,
});

export const setCurrentUserContentLimitExceededFlag = (exceeded) =>
    updateCurrentUser({
        changes: {
            contentLimit: {
                exceeded,
            },
        },
    });

export const userNavigate =
    ({ userId, newBoardId, previousBoardId, sync = true, operation, navigationSource }) =>
    (dispatch, getState) => {
        const state = getState();
        const elements = getElements(state);

        if (!isElementId(newBoardId)) return;

        const element = elements.get(newBoardId);
        const isQuickNotes = getRootType(element) === ROOT_TYPES.QUICK_NOTES;

        const aclIds = getAclIdsSelector(state);
        const userPermission = getPermission(elements, newBoardId, aclIds);

        const persist = !isQuickNotes && canRead(userPermission);

        const permissionId = getClosestPermissionIdForElementIdSelector()(state, { elementId: newBoardId });

        dispatch({
            type: USER_NAVIGATE,
            newBoardId,
            previousBoardId,
            permissionId,
            permission: userPermission,
            timestamp: getTimestamp(),
            userId,
            persist,
            sync,
            navigationSource,
            monitoring: {
                operation,
            },
        });
    };

export const currentUserNavigate =
    ({ newBoardId, previousBoardId, operation }) =>
    (dispatch, getState) => {
        const state = getState();
        const currentUserId = getCurrentUserId(state);
        return dispatch(userNavigate({ userId: currentUserId, newBoardId, previousBoardId, operation }));
    };

export const guestNavigate =
    ({ newBoardId, previousBoardId }) =>
    async (dispatch, getState) => {
        if (!isElementId(newBoardId)) return;

        const state = getState();

        const deviceId = getDeviceId();
        if (!deviceId) return;

        const permissionId = getClosestPermissionIdForElementIdSelector()(state, { elementId: newBoardId });

        const action = {
            type: GUEST_NAVIGATE,
            newBoardId,
            previousBoardId,
            deviceId,
            permissionId,
            timestamp: getTimestamp(),
        };

        dispatch(action);

        // FIXME-SOCKETS Won't need this once guests create a socket connection
        await http({
            url: 'actions',
            method: METHODS.POST,
            data: { action },
        });
    };

const sendUserNavigate =
    (user, boardId, navigationSource = 'web') =>
    (dispatch, getState) => {
        const state = getState();

        const currentBoardId = boardId || getCurrentBoardId(state);

        if (isGuest(user)) {
            return dispatch(guestNavigate({ newBoardId: currentBoardId }));
        }
        return dispatch(
            userNavigate({
                userId: getUserId(user),
                newBoardId: currentBoardId,
                operation: UserNavigationOperation.GENERAL,
                navigationSource,
            }),
        );
    };

/**
 * Retrieves the current user's board notification preferences and loads them into the store.
 */
const fetchNotificationPreferences = () => async (dispatch, getState) => {
    try {
        const response = await fetchCurrentUserBoardNotificationPreferencesHttp();
        const { boardNotificationPreferences } = response.data;
        dispatch(loadBoardNotificationPreferences({ boardNotificationPreferences }));
    } catch (error) {
        // Sensitive data audit: SAFE — Error object is sanitised automatically
        logger.error("Failed to fetch current user's board notification preferences", error);
    }
};

export const fetchCurrentUser = () => async (dispatch, getState) => {
    const authState = getState();
    const { hasLegacyToken, hasBetterAuthSession } = getCurrentUserAuthSessionState(authState);

    if (!hasLegacyToken && !hasBetterAuthSession) {
        // Sensitive data audit: SAFE — static message only
        logger.debug('Skipping current user fetch (no legacy token or stored Better Auth session user)');
        dispatch(setCurrentUserGuest());
        dispatch(appSetInitialised({ currentUser: true }));
        return;
    }

    dispatch({ type: CURRENT_USER_GET_DETAILS });

    try {
        const response = await fetchMe(getFetchMeConfig(hasBetterAuthSession));

        const state = getState();
        const { analytics, user, elements, betterAuthSession } = response.data;

        // If the current board id is not set yet at this stage and we're not using the swift hybrid app,
        // restore the last viewed board (if any), otherwise go to root board
        let boardId = getLocalLastNavigationId();
        if (!getIsCurrentBoardIdInitialised(state) && !isBoardPreviewEnabledSelector(state)) {
            boardId = boardId || user.lastViewedBoard || user.rootBoardId;
            dispatch(
                currentBoardIdSet({
                    boardId,
                    restored: !!user.lastViewedBoard,
                }),
            );
        }

        dispatch(setCurrentUser(user));
        dispatch(setUserAnalytics(analytics));

        // Refresh the remembered Better Auth session user (which carries the server-authoritative
        // session expiry) on every successful authentication, so a future lapsed session can be
        // recognised as a returning user rather than a fresh visitor, and we can measure how long
        // after expiry they returned. Capturing expiry each load also tracks updateAge slides.
        if (hasBetterAuthSession) {
            setRememberedBetterAuthSessionUser(user, { sessionExpiresAt: betterAuthSession?.expiresAt });
        }

        if (hasLegacyToken) {
            try {
                await upgradeLegacySessionToBetterAuthIfFlagged(user);
            } catch (migrationError) {
                logger.warn('Failed to upgrade legacy session to Better Auth', migrationError);
            }
        }

        if (hasBetterAuthSession) {
            try {
                await downgradeBetterAuthSessionToLegacyIfUnflagged(user);
            } catch (migrationError) {
                logger.warn('Failed to downgrade Better Auth session to legacy auth', migrationError);
            }
        }

        dispatch(appSetInitialised({ currentUser: true }));
        dispatch(sendUserNavigate(user, boardId));

        dispatch(loadElements(elements));
    } catch (err) {
        if (isUnauthenticatedCurrentUserError(err)) {
            if (hasLegacyToken) {
                // Sensitive data audit: SAFE — Static message about token validity, no user data exposed
                logger.error("Failed to validate current user's legacy token (no longer valid)");
                await dispatch(simpleLogout());
                return;
            }

            if (shouldReportCurrentUserAuthFailure({ error: err })) {
                // Sensitive data audit: SAFE — Static message about token validity, no user data exposed
                logger.error("Failed to validate current user's Better Auth session (no longer valid)");
            } else {
                // Sensitive data audit: SAFE — static message only
                logger.debug('No legacy token or Better Auth session found while fetching current user');
            }

            // A returning previously-authenticated user landing here unauthenticated indicates a lapsed
            // Better Auth session (a genuine expiry is otherwise indistinguishable from a fresh visitor
            // server-side). Report the forced logout to Amplitude before clearing session state.
            trackForcedLogoutIfLapsed();

            await clearInvalidBetterAuthSession();
            dispatch(setCurrentUserGuest());
            dispatch(appSetInitialised({ currentUser: true }));
            return;
        }

        // Sensitive data audit: SAFE — warn/error auto-sanitized (response and error sanitized; auth data redacted)
        logger.error('Failed to fetch current user', err, { response: err.response });
        dispatch({ type: CURRENT_USER_GET_FAILURE });
        dispatch(navigateToErrorPage());
    }
};

export const initialiseAppForCurrentUser = (getTimezone) => async (dispatch, getState) => {
    // Only get the timezone if we're initialising a browser page (not a puppeteer preview page, etc)
    const timezone = getTimezone ? getTimezone() : undefined;
    const disableCache = shouldDisableCache();

    dispatch(fetchNotificationPreferences());
    const response = await fetchCurrentUserAppInitialisationData({ timezone, disableCache });

    const { elements, remoteUserIds, elementCount, boardHierarchies } = response.data;

    const userIds = keys(remoteUserIds);
    const userShareCounts = mapValues(remoteUserIds, (shareCount) => ({
        shareCount,
    }));

    dispatch(loadElements(elements));
    dispatch(trackUsers(userIds));
    dispatch(loadUsers(userShareCounts));
    boardHierarchies && dispatch(boardHierarchiesLoad(boardHierarchies));

    !!elementCount && dispatch(setElementCount(elementCount));

    return response.data;
};

/**
 * Fetches the custom templates within this user's tree.
 */
export const fetchCurrentUserInitialCustomTemplates = () => async (dispatch, getState) => {
    try {
        const response = await fetchCurrentUserInitialCustomTemplatesHttp();

        const { elements } = response.data;

        const elementsArray = Object.values(elements);

        if (!isEmpty(elementsArray)) {
            dispatch(loadElements(elementsArray));
            dispatch(appSetInitialised({ customTemplates: true }));
        }
    } catch (e) {
        // Sensitive data audit: SAFE — Error object is sanitised automatically
        logger.error('Failed to fetch current user custom templates', e);
    }
};

// TODO-CHECKOUT: Finish what is required here — do we still need this action?
export const refreshCurrentUser = () => (dispatch) => dispatch(fetchCurrentUserSubscription());

export const updateCurrentUserHttp = (userChanges) => async (dispatch) => {
    const updatedUser = await updateMe({ userChanges });

    // NOTE: An action from the server will be received as well, but this ensures that the client is updated
    //  in case there's any socket message delivery issues
    dispatch(
        updateCurrentUser({
            userId: updatedUser._id,
            changes: updatedUser,
            sync: false,
        }),
    );
};

export const updateCurrentUserEmailHttp =
    ({ email, password }) =>
    async (dispatch) => {
        const { token, ...updatedUser } = await updateCurrentUserEmail({ email, password });

        if (token) {
            setCurrentUserToken(token);
        }

        // NOTE: An action from the server will be received as well, but this ensures that the client is updated
        //  in case there's any socket message delivery issues
        dispatch(
            updateCurrentUser({
                userId: updatedUser._id,
                changes: updatedUser,
                sync: false,
            }),
        );

        return updatedUser;
    };

export const setCurrentUserGuest = () => ({ type: CURRENT_USER_SET_GUEST });

const commonImageAvatarUpload = commonImageUpload({});

const CLEARED_IMAGE_SIZE_URLS = IMAGE_TYPE_CONFIGS[IMAGE_TYPES.AVATAR].sizes.reduce(
    (acc, size) => ({
        ...acc,
        [size.name]: null,
    }),
    {},
);

export const uploadCurrentUserAvatar =
    ({ userId, file, transactionId = getNewTransactionId() }) =>
    (dispatch) => {
        dispatch(commonImageAvatarUpload({ id: userId, file, transactionId, imageType: IMAGE_TYPES.AVATAR }))
            .then(({ uploadUrl, size }) => {
                dispatch(
                    updateCurrentUser({
                        userId,
                        changes: {
                            image: {
                                ...CLEARED_IMAGE_SIZE_URLS,
                                ...size,
                                original: uploadUrl,
                            },
                        },
                    }),
                );
            })
            // The error is already handled in the common image upload
            .catch((err) => null);
    };

export const fetchCurrentUserReferrals = () => (dispatch, getState) =>
    http({ url: 'users/me/referrals' })
        .then(({ data }) => {
            const userId = getState().getIn(['app', 'currentUser', '_id']);
            const { users, referrals } = data;

            if (users) dispatch(loadUsers(users));

            dispatch(
                updateCurrentUser({
                    userId,
                    changes: {
                        referrals,
                    },
                    sync: false,
                }),
            );
        })
        .catch((err) => null); // not a big deal if this fails

export const fetchCurrentUserElementCounts = () => async (dispatch, getState) => {
    try {
        const response = await http({ url: 'users/me/counts', timeout: 2 * TIMES.MINUTE });
        const { data } = response;

        const counts = get('counts', data);
        const contentLimit = get('contentLimit', data);

        if (counts) {
            dispatch(setElementCount(counts));
            dispatch(appSetInitialised({ elementCounts: true }));
            dispatch(updateCurrentUser({ changes: { contentLimit }, sync: false }));
        }

        return data;
    } catch (error) {
        // Sensitive data audit: SAFE — error auto-sanitized (count fetch failure)
        logger.error('Failed to retrieve user counts', error);
    }

    return null;
};
